import { createApi } from '@reduxjs/toolkit/query/react';
import { storeSavedSearch } from './helpers/saved-searches-storage';
import qs from 'qs';
import type {
    BlueshiftRecommendations4ApiResponse,
    GetFavouriteSellersAndItems2ApiResponse,
    ExecuteSavedSearchApiResponse,
    ExecuteSavedSearchForCurrentUserApiRequest,
    GetSavedSearchesApiResponse,
    FavouritesellersAndItems2Request,
    RecommendationsV4Request,
    ItemsByIdsApiResponse,
    ItemsItemsByIdsRequest,
    CategoryPageApiRequest,
    CategoryPageApiResponse,
    BrandSuggestionsApiResponse,
    IdeasApiResponse,
    TrendingSearchesApiResponse,
    TrendingSearchViewModel,
    CausePageApiRequest,
    CausePageApiResponse,
    StartPageSellersOfInterestRequest,
    SellersOfInterestApiResponse,
    StartPageIdeasRequest,
    StartPageTrendingSearchesRequest,
    InstagramFeedApiResponse,
    InstagramFeedApiRequest
} from './types/webapi-discover-generated';
import { isServer } from 'tradera/utils/nextjs';
import { isomorphicBaseQueryForWishListableItems } from './utils/webapi-base-query';
import type { RootState } from 'tradera/state/configure-store';
import { selectIsBot } from 'tradera/state/request/selectors';

export type RecommendationsQueryParams = {
    slot: string;
    userLanguage: string;
    automaticTranslationPreferred: boolean;
    shippingCountryCode: string;
    maxResults: number;
    pageNumber: number;
    seedItemIds?: number[];
    seedCategoryId?: number;
};

export type SuggestedRecommendationsQueryParams = {
    slot: string;
    userLanguage: string;
    automaticTranslationPreferred: boolean;
    shippingCountryCode: string;
    maxResults?: number;
    pageNumber?: number;
    seedItemIds?: number[];
    seedCategoryId?: number;
};

/**
 * Creates a query params object for RTK Query.
 * The query parameters for RTK Query is used as key for caching hence
 * must always be the same for the same query on server and client.
 */
export const getRecommendationsQueryParams = ({
    slot,
    userLanguage,
    automaticTranslationPreferred,
    shippingCountryCode,
    maxResults = 36,
    pageNumber = 1,
    seedItemIds,
    seedCategoryId
}: SuggestedRecommendationsQueryParams): RecommendationsQueryParams => {
    const params: RecommendationsQueryParams = {
        slot,
        maxResults,
        userLanguage: userLanguage.toLowerCase(),
        automaticTranslationPreferred,
        shippingCountryCode: shippingCountryCode.toUpperCase(),
        pageNumber
    };

    if (seedItemIds?.length) {
        params.seedItemIds = seedItemIds;
    }
    if (seedCategoryId) {
        params.seedCategoryId = seedCategoryId;
    }

    return params;
};

type FavouriteSellersQueryParams = {
    shippingCountryCode: string;
    userLanguage: string;
    automaticTranslationPreferred: boolean;
};

/**
 * Creates a query params object for RTK Query.
 * The query parameters for RTK Query is used as key for caching hence
 * must always be the same for the same query on server and client.
 */
export const getFavouriteSellersQueryParams = ({
    shippingCountryCode,
    userLanguage,
    automaticTranslationPreferred
}: FavouriteSellersQueryParams): FavouriteSellersQueryParams => ({
    shippingCountryCode: shippingCountryCode.toUpperCase(),
    userLanguage: userLanguage.toLowerCase(),
    automaticTranslationPreferred
});

/**
 * Creates a query params object for RTK Query.
 * The query parameters for RTK Query is used as key for caching hence
 * must always be the same for the same query on server and client.
 */
export const getInstagramQueryParams = () => ({});

export const discoverApi = createApi({
    reducerPath: 'discoveryApi',
    baseQuery: isomorphicBaseQueryForWishListableItems,
    endpoints: (builder) => ({
        getBrandSuggestions: builder.query<
            BrandSuggestionsApiResponse,
            unknown
        >({
            query: () => {
                const baseUrl = isServer ? '' : '/api/webapi';
                return `${baseUrl}/discover/web/start-page/brand-suggestions`;
            },
            extraOptions: {
                getWishListableItems: () => null
            }
        }),
        getFavouriteSellers: builder.query<
            GetFavouriteSellersAndItems2ApiResponse,
            FavouritesellersAndItems2Request
        >({
            query: ({
                shippingCountryCode,
                userLanguage,
                automaticTranslationPreferred,
                maxItemsPerFavouriteSeller = 3,
                maxItems = 20,
                maxFavouriteSellers = 20
            }) => {
                const query = qs.stringify({
                    shippingCountryCode,
                    userLanguage,
                    automaticTranslationPreferred,
                    maxItemsPerFavouriteSeller,
                    maxItems,
                    maxFavouriteSellers
                });
                const baseUrl = isServer ? '' : '/api/webapi';
                return `${baseUrl}/discover/favouritesellers-and-items2?${query}`;
            },
            extraOptions: {
                getWishListableItems: () => null
            }
        }),
        getSellersOfInterest: builder.query<
            SellersOfInterestApiResponse,
            StartPageSellersOfInterestRequest
        >({
            query: ({ shippingCountryCode, maxSellers = 20 }) => {
                const query = qs.stringify({
                    shippingCountryCode,
                    maxSellers
                });
                const baseUrl = isServer ? '' : '/api/webapi';
                return `${baseUrl}/discover/web/start-page/sellers-of-interest?${query}`;
            },
            extraOptions: {
                getWishListableItems: () => null
            }
        }),
        getIdeas: builder.query<IdeasApiResponse, StartPageIdeasRequest>({
            query: ({ languageCodeIso2 }) => {
                const baseUrl = isServer ? '' : '/api/webapi';
                return `${baseUrl}/discover/web/start-page/ideas?languageCodeIso2=${languageCodeIso2}`;
            },
            extraOptions: {
                getWishListableItems: () => null
            }
        }),
        getInstagramFeed: builder.query<
            InstagramFeedApiResponse,
            InstagramFeedApiRequest
        >({
            query: () => {
                const baseUrl = isServer ? '' : '/api/webapi';
                return `${baseUrl}/discover/web/start-page/instagram-feed`;
            },
            extraOptions: {
                getWishListableItems: () => null
            }
        }),
        getTrendingSearches: builder.query<
            TrendingSearchViewModel[],
            StartPageTrendingSearchesRequest
        >({
            queryFn: async (
                { languageCodeIso2 },
                { getState },
                _extraOptions,
                baseQuery
            ) => {
                // Never fetch trending searches for bots because
                // if language is not Swedish translation will be expensive
                // due to the volume of bot requests.
                if (selectIsBot(getState() as RootState)) {
                    return { data: [] as TrendingSearchViewModel[] };
                }

                const baseUrl = isServer ? '' : '/api/webapi';
                const { data } = await baseQuery(
                    `${baseUrl}/discover/web/start-page/trending-searches?languageCodeIso2=${languageCodeIso2}`
                );
                return {
                    data: ((data as TrendingSearchesApiResponse).searches ||
                        []) as TrendingSearchViewModel[]
                };
            },
            extraOptions: {
                getWishListableItems: () => null
            }
        }),
        getSavedSearches: builder.query<GetSavedSearchesApiResponse, unknown>({
            query: () => {
                const baseUrl = isServer ? '' : '/api/webapi';
                return `${baseUrl}/discover/savedsearches`;
            },
            extraOptions: {
                getWishListableItems: () => null
            }
        }),
        getRecommendations: builder.query<
            BlueshiftRecommendations4ApiResponse,
            RecommendationsV4Request
        >({
            query: ({
                slot,
                shippingCountryCode,
                userLanguage,
                automaticTranslationPreferred,
                maxResults,
                pageNumber,
                seedItemIds,
                seedCategoryId
            }: RecommendationsQueryParams) => {
                const query = qs.stringify({
                    slot,
                    pageNumber,
                    shippingCountryCode,
                    userLanguage,
                    automaticTranslationPreferred,
                    maxResults,
                    seedItemIds,
                    seedCategoryId
                });
                const baseUrl = isServer ? '' : '/api/webapi';
                return `${baseUrl}/discover/recommendations-v4?${query}`;
            },
            extraOptions: {
                getWishListableItems: (
                    response: BlueshiftRecommendations4ApiResponse
                ) => response.items
            }
        }),
        getCategoryResult: builder.query<
            CategoryPageApiResponse,
            CategoryPageApiRequest
        >({
            query: (parameters) => {
                const baseUrl = isServer ? '' : '/api/webapi';

                return {
                    url: `${baseUrl}/discover/web/category-page`,
                    method: 'POST',
                    body: parameters
                };
            },
            extraOptions: {
                getWishListableItems: (response: CategoryPageApiResponse) =>
                    response.items
            }
        }),
        getSavedSearchResults: builder.query<
            ExecuteSavedSearchApiResponse,
            ExecuteSavedSearchForCurrentUserApiRequest
        >({
            query: (parameters) => {
                const baseUrl = isServer ? '' : '/api/webapi';

                return {
                    url: `${baseUrl}/discover/savedsearches/search-v2`,
                    method: 'POST',
                    body: parameters
                };
            },
            extraOptions: {
                //response.response?.result?.items are of type SearchResultItem2ApiDto which does not currently have the "inWishList" property
                getWishListableItems: () => null
            },
            transformResponse: (
                response: ExecuteSavedSearchApiResponse,
                meta,
                arg
            ) => {
                if (isServer) return response;

                storeSavedSearch(
                    arg.id,
                    response.response?.result?.itemsMatched || 0
                );
                return response;
            }
        }),
        getItemsByIds: builder.query<
            ItemsByIdsApiResponse,
            ItemsItemsByIdsRequest
        >({
            query: (params) => {
                const baseUrl = isServer ? '' : '/api/webapi';

                const query = qs.stringify(params);
                return `${baseUrl}/discover/web/items/items-by-ids?${query}`;
            },
            extraOptions: {
                getWishListableItems: (response: ItemsByIdsApiResponse) =>
                    response.items
            }
        }),
        getCausePage: builder.query<CausePageApiResponse, CausePageApiRequest>({
            query: (params) => {
                return {
                    url: '/api/webapi/discover/web/custom-result-pages/cause-page',
                    method: 'POST',
                    body: params
                };
            },
            extraOptions: {
                getWishListableItems: (response: ItemsByIdsApiResponse) =>
                    response.items
            }
        })
    })
});

// KATS: break this up into more files, do not append more endpoints to this file,
// we have decided that it's an anti-pattern to have a single file with all endpoints.
// https://traderasweden.slack.com/archives/C01BUB6G7UJ/p1699018076088839?thread_ts=1698937051.165139&cid=C01BUB6G7UJ
export const {
    useGetBrandSuggestionsQuery,
    useGetIdeasQuery,
    useGetSellersOfInterestQuery,
    useGetInstagramFeedQuery,
    useGetCategoryResultQuery,
    useGetSavedSearchesQuery,
    useGetSavedSearchResultsQuery,
    useGetRecommendationsQuery,
    useGetFavouriteSellersQuery,
    useGetTrendingSearchesQuery,
    useGetItemsByIdsQuery,
    useGetCausePageQuery,
    endpoints
} = discoverApi;

export const { updateQueryData } = discoverApi.util;
